import React, { useContext, useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Row,
  Select,
  Statistic,
  Table,
  Tabs,
  Switch,
  Space,
  Typography,
} from "antd";
import {
  DollarOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  UnorderedListOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useParams } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
} from "recharts";
import { useInvoicedVsTimeReport } from "../../hooks/useInvoicedVsTimeReport";
import {
  InvoicedVsTimeFilters,
  InvoicedVsTimeSummary,
  InvoiceListItem,
  TimeActivityListItem,
  PaginatedResponse,
} from "../../interfaces/interfaces";
import DataContext from "../../context/DataContext";
import { CardTitle, ColumnComponent } from "../../components/ui";

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text } = Typography;

type DatePreset = "today" | "lastWeek" | "lastMonth" | "2024" | "2023" | "2022" | "ytd" | "last12" | "custom";

export const InvoicedVsTimeReport = () => {
  const { idClient } = useParams<{ idClient: string }>();
  const { customerByClient, getData } = useContext(DataContext);
  const { getSummary, getInvoices, getTimeActivities, getEmployees } =
    useInvoicedVsTimeReport();

  // Filter state
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
  const [datePreset, setDatePreset] = useState<DatePreset>("last12");
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [invoiceStatus, setInvoiceStatus] = useState<"active" | "all">("all");

  // Data state
  const [summary, setSummary] = useState<InvoicedVsTimeSummary | null>(null);
  const [invoiceData, setInvoiceData] =
    useState<PaginatedResponse<InvoiceListItem> | null>(null);
  const [timeActivityData, setTimeActivityData] =
    useState<PaginatedResponse<TimeActivityListItem> | null>(null);
  const [loading, setLoading] = useState(false);

  // Pagination state
  const [invoicePage, setInvoicePage] = useState(1);
  const [timeActivityPage, setTimeActivityPage] = useState(1);
  const [pageSize] = useState(25);

  // Employee list loaded from Employee model
  const [employeeOptions, setEmployeeOptions] = useState<
    { value: string; label: string }[]
  >([]);

  useEffect(() => {
    if (idClient) {
      getData("customer", idClient);
      loadEmployees();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idClient]);

  const loadEmployees = async () => {
    if (!idClient) return;
    try {
      const employees = await getEmployees(idClient);
      setEmployeeOptions(employees || []);
    } catch (error) {
      console.error("Error loading employees:", error);
      setEmployeeOptions([]);
    }
  };

  // Calculate date range based on preset
  const getDateRangeFromPreset = (preset: DatePreset): [Dayjs, Dayjs] => {
    const now = dayjs();
    switch (preset) {
      case "today":
        return [now.startOf("day"), now.endOf("day")];
      case "lastWeek":
        return [now.subtract(7, "day"), now];
      case "lastMonth":
        return [now.subtract(30, "day"), now];
      case "2024":
        return [dayjs("2024-01-01"), dayjs("2024-12-31")];
      case "2023":
        return [dayjs("2023-01-01"), dayjs("2023-12-31")];
      case "2022":
        return [dayjs("2022-01-01"), dayjs("2022-12-31")];
      case "ytd":
        return [dayjs().startOf("year"), now];
      case "last12":
        return [now.subtract(12, "month"), now];
      case "custom":
        return dateRange || [now.subtract(12, "month"), now];
      default:
        return [now.subtract(12, "month"), now];
    }
  };

  const handleApplyFilters = async () => {
    if (!idClient) return;

    setLoading(true);
    const [from, to] = getDateRangeFromPreset(datePreset);

    const filters: InvoicedVsTimeFilters = {
      from: from.toDate(),
      to: to.toDate(),
      customerIds: selectedCustomers.length > 0 ? selectedCustomers : null,
      employeeIds: selectedEmployees.length > 0 ? selectedEmployees : null,
      invoiceStatus,
      groupBy: "month",
    };

    try {
      // Fetch summary
      const summaryData = await getSummary(idClient, filters);
      setSummary(summaryData);

      // Fetch first page of invoices
      const invoices = await getInvoices(idClient, filters, {
        page: 1,
        limit: pageSize,
      });
      setInvoiceData(invoices);
      setInvoicePage(1);

      // Fetch first page of time activities
      const timeActivities = await getTimeActivities(idClient, filters, {
        page: 1,
        limit: pageSize,
      });
      setTimeActivityData(timeActivities);
      setTimeActivityPage(1);
    } catch (error) {
      console.error("Error fetching report data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInvoicePageChange = async (page: number) => {
    if (!idClient) return;
    setLoading(true);
    const [from, to] = getDateRangeFromPreset(datePreset);

    const filters: InvoicedVsTimeFilters = {
      from: from.toDate(),
      to: to.toDate(),
      customerIds: selectedCustomers.length > 0 ? selectedCustomers : null,
      invoiceStatus,
    };

    const invoices = await getInvoices(idClient, filters, {
      page,
      limit: pageSize,
    });
    setInvoiceData(invoices);
    setInvoicePage(page);
    setLoading(false);
  };

  const handleTimeActivityPageChange = async (page: number) => {
    if (!idClient) return;
    setLoading(true);
    const [from, to] = getDateRangeFromPreset(datePreset);

    const filters: InvoicedVsTimeFilters = {
      from: from.toDate(),
      to: to.toDate(),
      customerIds: selectedCustomers.length > 0 ? selectedCustomers : null,
      employeeIds: selectedEmployees.length > 0 ? selectedEmployees : null,
    };

    const timeActivities = await getTimeActivities(idClient, filters, {
      page,
      limit: pageSize,
    });
    setTimeActivityData(timeActivities);
    setTimeActivityPage(page);
    setLoading(false);
  };

  const handleReset = () => {
    setSelectedCustomers([]);
    setSelectedEmployees([]);
    setDatePreset("last12");
    setDateRange(null);
    setInvoiceStatus("all");
    setSummary(null);
    setInvoiceData(null);
    setTimeActivityData(null);
  };

  // Prepare chart data
  const chartData =
    summary?.timeline.periods.map((period, index) => ({
      period,
      invoiced: summary.timeline.invoicedAmt[index],
      hours: summary.timeline.reportedHours[index],
    })) || [];

  // Customer options
  const customerOptions =
    customerByClient.map((customer: any) => ({
      value: customer.Id,
      label: customer.DisplayName || customer.FullyQualifiedName,
    })) || [];

  // Invoice table columns
  const invoiceColumns = [
    {
      title: "Doc Number",
      dataIndex: "DocNumber",
      key: "DocNumber",
    },
    {
      title: "Date",
      dataIndex: "TxnDate",
      key: "TxnDate",
      render: (date: Date) => dayjs(date).format("MM/DD/YYYY"),
    },
    {
      title: "Customer",
      dataIndex: ["CustomerRef", "name"],
      key: "customer",
    },
    {
      title: "Amount",
      dataIndex: "TotalAmt",
      key: "TotalAmt",
      render: (amt: number) => `$${amt.toFixed(2)}`,
    },
    {
      title: "Status",
      dataIndex: "active",
      key: "active",
      render: (active: boolean) => (active ? "Active" : "Inactive"),
    },
  ];

  // Time activity table columns
  const timeActivityColumns = [
    {
      title: "Date",
      dataIndex: "TxnDate",
      key: "TxnDate",
      render: (date: Date) => dayjs(date).format("MM/DD/YYYY"),
    },
    {
      title: "Employee",
      dataIndex: ["EmployeeRef", "name"],
      key: "employee",
    },
    {
      title: "Customer",
      dataIndex: ["CustomerRef", "name"],
      key: "customer",
    },
    {
      title: "Hours",
      key: "hours",
      render: (_: any, record: TimeActivityListItem) => {
        const totalHours = (record.Hours || 0) + (record.Minutes || 0) / 60;
        return totalHours.toFixed(2);
      },
    },
    {
      title: "Rate",
      dataIndex: "HourlyRate",
      key: "HourlyRate",
      render: (rate: number) => (rate ? `$${rate.toFixed(2)}` : "-"),
    },
    {
      title: "Billable",
      dataIndex: "BillableStatus",
      key: "BillableStatus",
    },
    {
      title: "Description",
      dataIndex: "Description",
      key: "Description",
      ellipsis: true,
    },
  ];

  return (
    <div className="general-view">
      <Row gutter={[24, 24]}>
        <ColumnComponent>
          <CardTitle title="Invoiced vs Reported Time Report" />
        </ColumnComponent>

        {/* Filter Section */}
        <ColumnComponent lg={24} xl={24}>
          <Card title="Filters">
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={8}>
                <Text strong>Customer(s)</Text>
                <Select
                  mode="multiple"
                  allowClear
                  placeholder="All Customers"
                  style={{ width: "100%", marginTop: 8 }}
                  value={selectedCustomers}
                  onChange={setSelectedCustomers}
                  options={customerOptions}
                  showSearch
                  filterOption={(input, option) =>
                    String(option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Col>

              <Col xs={24} sm={12} md={8}>
                <Text strong>Employee(s)</Text>
                <Select
                  mode="multiple"
                  allowClear
                  placeholder="All Employees"
                  style={{ width: "100%", marginTop: 8 }}
                  value={selectedEmployees}
                  onChange={setSelectedEmployees}
                  options={employeeOptions}
                  showSearch
                  filterOption={(input, option) =>
                    String(option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Col>

              <Col xs={24} sm={12} md={8}>
                <Text strong>Date Range</Text>
                <Select
                  style={{ width: "100%", marginTop: 8 }}
                  value={datePreset}
                  onChange={(value) => {
                    setDatePreset(value);
                    if (value !== "custom") {
                      setDateRange(null);
                    }
                  }}
                >
                  <Option value="today">Today</Option>
                  <Option value="lastWeek">Last 7 Days</Option>
                  <Option value="lastMonth">Last 30 Days</Option>
                  <Option value="last12">Last 12 Months</Option>
                  <Option value="ytd">Year to Date</Option>
                  <Option value="2024">2024</Option>
                  <Option value="2023">2023</Option>
                  <Option value="2022">2022</Option>
                  <Option value="custom">Custom Range</Option>
                </Select>
              </Col>

              {datePreset === "custom" && (
                <Col xs={24} sm={12} md={8}>
                  <Text strong>Custom Date Range</Text>
                  <RangePicker
                    style={{ width: "100%", marginTop: 8 }}
                    value={dateRange}
                    onChange={(dates) => setDateRange(dates as [Dayjs, Dayjs])}
                  />
                </Col>
              )}

              <Col xs={24} sm={12} md={8}>
                <Text strong>Invoice Status</Text>
                <div style={{ marginTop: 8 }}>
                  <Space>
                    <Switch
                      checked={invoiceStatus === "active"}
                      onChange={(checked) =>
                        setInvoiceStatus(checked ? "active" : "all")
                      }
                    />
                    <Text>
                      {invoiceStatus === "active"
                        ? "Active Only"
                        : "All Invoices"}
                    </Text>
                  </Space>
                </div>
              </Col>

              <Col xs={24}>
                <Space>
                  <Button
                    type="primary"
                    icon={<ReloadOutlined />}
                    onClick={handleApplyFilters}
                    loading={loading}
                  >
                    Apply Filters
                  </Button>
                  <Button onClick={handleReset}>Reset</Button>
                </Space>
              </Col>
            </Row>
          </Card>
        </ColumnComponent>

        {/* KPI Cards */}
        {summary && (
          <>
            <Col xs={12} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Total Invoiced"
                  value={summary.kpis.totalInvoicedAmt}
                  precision={2}
                  prefix={<DollarOutlined />}
                />
              </Card>
            </Col>
            <Col xs={12} sm={12} md={6}>
              <Card>
                <Statistic
                  title="# Invoices"
                  value={summary.kpis.invoiceCount}
                  prefix={<FileTextOutlined />}
                />
              </Card>
            </Col>
            <Col xs={12} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Total Reported Hours"
                  value={summary.kpis.totalReportedHours}
                  precision={2}
                  prefix={<ClockCircleOutlined />}
                />
              </Card>
            </Col>
            <Col xs={12} sm={12} md={6}>
              <Card>
                <Statistic
                  title="# Time Entries"
                  value={summary.kpis.timeEntryCount}
                  prefix={<UnorderedListOutlined />}
                />
              </Card>
            </Col>
          </>
        )}

        {/* Timeline Chart */}
        {summary && chartData.length > 0 && (
          <ColumnComponent lg={24} xl={24}>
            <Card title="Timeline Comparison">
              <ResponsiveContainer width="100%" height={400}>
                <ComposedChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" />
                  <YAxis
                    yAxisId="left"
                    label={{
                      value: "Amount ($)",
                      angle: -90,
                      position: "insideLeft",
                    }}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    label={{
                      value: "Hours",
                      angle: 90,
                      position: "insideRight",
                    }}
                  />
                  <Tooltip />
                  <Legend />
                  <Bar
                    yAxisId="left"
                    dataKey="invoiced"
                    fill="#1890ff"
                    name="Invoiced Amount ($)"
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="hours"
                    stroke="#52c41a"
                    strokeWidth={2}
                    name="Reported Hours"
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </Card>
          </ColumnComponent>
        )}

        {/* Detail Tables */}
        {(invoiceData || timeActivityData) && (
          <ColumnComponent lg={24} xl={24}>
            <Card>
              <Tabs
                items={[
                  {
                    key: "invoices",
                    label: `Invoices (${invoiceData?.total || 0})`,
                    children: (
                      <Table
                        columns={invoiceColumns}
                        dataSource={invoiceData?.data || []}
                        rowKey="_id"
                        loading={loading}
                        pagination={{
                          current: invoicePage,
                          pageSize,
                          total: invoiceData?.total || 0,
                          onChange: handleInvoicePageChange,
                        }}
                      />
                    ),
                  },
                  {
                    key: "timeactivities",
                    label: `Time Activities (${timeActivityData?.total || 0})`,
                    children: (
                      <Table
                        columns={timeActivityColumns}
                        dataSource={timeActivityData?.data || []}
                        rowKey="_id"
                        loading={loading}
                        pagination={{
                          current: timeActivityPage,
                          pageSize,
                          total: timeActivityData?.total || 0,
                          onChange: handleTimeActivityPageChange,
                        }}
                      />
                    ),
                  },
                ]}
              />
            </Card>
          </ColumnComponent>
        )}
      </Row>
    </div>
  );
};
